/**
 * Bio context — the CONTEXTUAL FACTS about how a creator's bio is being served,
 * resolved ONCE per page from the `[url_code]` route param and threaded
 * (server-side) to every component that builds an in-bio URL. No client-side
 * `useParams`/`useRouter` guesswork: the render decides how to navigate from data
 * it already holds.
 *
 * It carries only SOURCE facts — the creator's url code and whether we're on an
 * independent custom domain. It deliberately does NOT bake in presentation
 * (`linkBase` / `modalBase` prefixes): those are a rendering decision derived by
 * the consumer ({@link bioUrl}) from these facts. Keeping the context to raw
 * facts means a future navigation flag (e.g. `bot?: boolean` to emit crawlable
 * prefix-free links) is a one-line addition here that every URL automatically
 * respects — no re-threading.
 *
 * The url code is carried IN the param — `proxy.ts` sets the `~<urlCode>` sentinel
 * from the Fastly header (routing layer) so this resolves SYNCHRONOUSLY by prefix
 * and the render NEVER reads a request header → the bio render stays CDN-cacheable.
 *
 * `isIndependent` is host-dependent, so a `BioContext` — like the URLs built from
 * it — must NEVER enter a `'use cache'` boundary; keep it a render-time prop,
 * split from the clean `urlCode` used for data loads / cache tags.
 */
import { PopStoreBioDomainType } from '@ps/models/lib/profile';

/**
 * Prefix marking an independent-domain `[url_code]` param — `~<urlCode>` (e.g.
 * `~andy.us1`). `proxy.ts` sets it from the Fastly header; the render strips it to
 * recover the clean url code. Also the bare soft-nav sentinel (`/~/…`). Not a
 * valid char in a real url code.
 */
export const INDEPENDENT_URL_CODE_PREFIX = '~';

export interface BioContext {
  /** Clean url code — the creator identifier for URLs, cache tags, links. */
  urlCode: string;
  /**
   * The creator's store/profile id, resolved from the url code. Carried on the
   * context so URL builders that need the immutable id (e.g. the legacy
   * `/products/<creatorId>/<listingId>` product route) don't re-resolve it.
   */
  creatorId: string;
  /**
   * True when served on an independent custom domain, where the domain root IS
   * the bio home (so ordinary links are prefix-free and intercepting-modal links
   * use the `~` soft-nav sentinel). {@link bioUrl} reads this to pick the prefix
   * of RELATIVE (nav) links — it reflects the CURRENT host.
   */
  isIndependent: boolean;
  /**
   * The creator's canonical independent custom domain (`profile.bioDomain`), e.g.
   * `test2.awesomeapps.dev` — absent when they have none. It's the creator FACT
   * (independent of the current host), so {@link bioUrl} builds ABSOLUTE share
   * links against it (`https://<domain>`, prefix-free) deterministically —
   * server-side too, with no reliance on `window.location.origin`. Absent → share
   * links use the default pop-store host. Effectively immutable, so it's safe to
   * carry from the (cached) profile.
   */
  domain?: string;
  /**
   * The creator's chosen ALTERNATIVE domain — a pop-store-provided alias that
   * works exactly like the default host (url-code prefix, same routing), just a
   * nicer name. Absent when they use the plain default. Unlike {@link domain}
   * (independent), it's host-conditional: {@link bioUrl} uses it for ABSOLUTE
   * share links ONLY when the visitor is currently on it (validated against this
   * value, never reflecting an arbitrary host), else the default host.
   */
  domainAlias?: string;
}

/**
 * Split a creator's `bioDomain` into the {@link BioContext} domain fields: an
 * INDEPENDENT custom domain becomes `domain` (its own canonical host, prefix-
 * free); an ALTERNATIVE pop-store alias becomes `domainAlias` (default-host-like,
 * used only when the visitor is on it). One place owns this mapping.
 */
export function bioDomains(
  bioDomain:
    | { domainName: string; type: PopStoreBioDomainType }
    | null
    | undefined,
): Pick<BioContext, 'domain' | 'domainAlias'> {
  if (!bioDomain) {
    return {};
  }
  return bioDomain.type === PopStoreBioDomainType.Independent
    ? { domain: bioDomain.domainName }
    : { domainAlias: bioDomain.domainName };
}

/** The url-code route facts parsed from a `[url_code]` param, before the profile
 *  (and thus `creatorId`) is resolved. */
export interface BioRouteParam {
  urlCode: string;
  isIndependent: boolean;
}

/** True when the `[url_code]` param carries the independent-domain sentinel. */
export function isIndependentRouteParam(urlCodeParam: string): boolean {
  return urlCodeParam.startsWith(INDEPENDENT_URL_CODE_PREFIX);
}

/**
 * Parse a `[url_code]` route param into its route facts (clean url code +
 * independent-domain flag). Pure and SYNCHRONOUS — the url code comes from the
 * param itself (never a request header), so the bio render stays deterministic
 * and CDN-cacheable. The async {@link resolveBioContext} (apps/web) layers the
 * resolved `creatorId` on top to form the full {@link BioContext}.
 *
 * Independent (`~andy.us1`): `{ urlCode: 'andy.us1', isIndependent: true }`.
 * Default/alias (`andy.us1`): `{ urlCode: 'andy.us1', isIndependent: false }`.
 */
export function parseBioRouteParam(urlCodeParam: string): BioRouteParam {
  if (isIndependentRouteParam(urlCodeParam)) {
    return {
      urlCode: urlCodeParam.slice(INDEPENDENT_URL_CODE_PREFIX.length),
      isIndependent: true,
    };
  }
  return { urlCode: urlCodeParam, isIndependent: false };
}

/**
 * Resolve the {@link BioContext} for a CREATOR-OWNED surface (the editor /
 * dashboard) from the host it's served on + the creator's configured independent
 * domain. Independent when we're actually being served on that custom domain — so
 * the editor preview builds the SAME custom-domain-aware links a visitor gets
 * there (e.g. a product opens on the creator's own domain), and the ordinary
 * default-domain form otherwise. No hardcoded assumption about the domain.
 *
 * Pure: the caller reads the host (`window.location.host`, client-only) and
 * passes it with the creator's `bioDomain`. Visitor surfaces use
 * {@link resolveBioContext} instead (the host is resolved by the proxy into the
 * route param).
 */
export function bioContextForHost(params: {
  urlCode: string;
  /** The creator's store/profile id. */
  creatorId: string;
  /** The creator's configured bio domain (independent or alternative), if any. */
  bioDomain?: { domainName: string; type: PopStoreBioDomainType } | null;
  /** The host the surface is currently served on (`window.location.host`). */
  host?: string | null;
}): BioContext {
  const { urlCode, creatorId, bioDomain, host } = params;
  const domains = bioDomains(bioDomain);
  return {
    urlCode,
    creatorId,
    // `isIndependent` reflects the CURRENT host (drives nav-link shape); the
    // domain fields (`domain` / `domainAlias`) are creator facts, so an absolute
    // link from the dashboard still points at the right public host even when nav
    // links are the default-host form.
    isIndependent: domains.domain != null && host === domains.domain,
    ...domains,
  };
}
